/**
 * Course Editor illustration — miniature wireframe of the studio course editor:
 * sidebar tree nav + main content area with tabs and lesson cards.
 */
import type { IllustrationProps } from './types';
import { usePalette } from './types';

export function CourseEditor({ variant = 'on-gradient', className, style }: IllustrationProps) {
  const p = usePalette(variant);

  return (
    <svg
      viewBox="0 0 140 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {/* Frame */}
      <rect
        x="4"
        y="4"
        width="132"
        height="112"
        rx="10"
        fill={p.frameFill}
        stroke={p.frameStroke}
        strokeWidth="1"
      />

      {/* Sidebar — content tree */}
      <rect x="4" y="4" width="38" height="112" rx="10" fill={p.cardFill} />
      <rect x="42" y="4" width="1" height="112" fill={p.cardStroke} />

      {/* Sidebar: course title */}
      <rect x="10" y="14" width="26" height="3" rx="1.5" fill={p.strong} opacity="0.3" />

      {/* Module 1 — expanded */}
      <rect x="10" y="24" width="22" height="2.5" rx="1.25" fill={p.subtle} />
      {/* Topics indented */}
      <rect x="16" y="30" width="18" height="2" rx="1" fill={p.faint} />
      <rect x="16" y="35" width="16" height="2" rx="1" fill={p.accent1} opacity="0.5" />
      <rect x="16" y="40" width="20" height="2" rx="1" fill={p.faint} />

      {/* Module 2 — collapsed */}
      <rect x="10" y="48" width="24" height="2.5" rx="1.25" fill={p.subtle} />

      {/* Module 3 — collapsed */}
      <rect x="10" y="54" width="20" height="2.5" rx="1.25" fill={p.subtle} />

      {/* Main content area */}
      {/* Tabs: Content | Overview | Settings | Learners */}
      <rect x="50" y="12" width="20" height="3" rx="1.5" fill={p.accent1} opacity="0.6" />
      <rect x="50" y="17" width="20" height="0.75" rx="0.375" fill={p.accent1} opacity="0.8" />
      <rect x="74" y="12" width="22" height="3" rx="1.5" fill={p.faint} />
      <rect x="100" y="12" width="18" height="3" rx="1.5" fill={p.faint} />

      {/* Topic header area */}
      <rect x="50" y="26" width="48" height="4" rx="2" fill={p.strong} opacity="0.2" />
      <rect x="50" y="34" width="32" height="2.5" rx="1.25" fill={p.faint} />

      {/* Lesson cards */}
      <rect
        x="50"
        y="44"
        width="80"
        height="16"
        rx="4"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="56" y="48" width="36" height="3" rx="1.5" fill={p.subtle} />
      <rect x="56" y="54" width="24" height="2" rx="1" fill={p.faint} />
      {/* Step count badge */}
      <rect x="110" y="48" width="14" height="4" rx="2" fill={p.accent1} opacity="0.25" />

      <rect
        x="50"
        y="64"
        width="80"
        height="16"
        rx="4"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="56" y="68" width="42" height="3" rx="1.5" fill={p.subtle} />
      <rect x="56" y="74" width="28" height="2" rx="1" fill={p.faint} />
      <rect x="110" y="68" width="14" height="4" rx="2" fill={p.accent2} opacity="0.25" />

      <rect
        x="50"
        y="84"
        width="80"
        height="16"
        rx="4"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="56" y="88" width="30" height="3" rx="1.5" fill={p.subtle} />
      <rect x="56" y="94" width="20" height="2" rx="1" fill={p.faint} />
      <rect x="110" y="88" width="14" height="4" rx="2" fill={p.accent3} opacity="0.25" />

      {/* Publish button */}
      <rect x="100" y="104" width="30" height="8" rx="4" fill={p.buttonFill} />
      <rect x="107" y="107" width="16" height="2.5" rx="1.25" fill={p.buttonText} />
    </svg>
  );
}
